import type { DrawingDocument, NormalizedPoint } from '../model';
import { distanceBetween } from './bonds.ts';
import { changeDepiction, roundCoordinate } from './drawingDocumentCore.ts';

// Rounding to COORDINATE_DECIMAL_PLACES introduces at most ~0.01-0.02 units of length noise on a
// rigid rotate/flip transform; this must stay comfortably above that floor and comfortably below
// any real drag, or a rigid transform's rounding noise would get flagged as a length override.
const BOND_LENGTH_OVERRIDE_TOLERANCE = 0.1;

export function moveAtom(document: DrawingDocument, nodeId: string, position: NormalizedPoint): DrawingDocument {
  return moveAtoms(document, { [nodeId]: position });
}

/**
 * Updates 2D depiction coordinates without changing authoritative chemistry. Bonds whose
 * lengths change are marked as explicit depiction overrides.
 */
export function moveAtoms(
  document: DrawingDocument,
  positions: Readonly<Record<string, NormalizedPoint>>
): DrawingDocument {
  const coordinateSet = document.depiction.coordinateSet;
  const coordinates = { ...coordinateSet.coordinates };
  const moved = new Set<string>();
  for (const [nodeId, position] of Object.entries(positions)) {
    if (!coordinates[nodeId] || !Number.isFinite(position.x) || !Number.isFinite(position.y)) continue;
    // #805: not clamped to the coordinate space -- the page grows to hold whatever is moved.
    const next = {
      x: roundCoordinate(position.x * coordinateSet.width),
      y: roundCoordinate(position.y * coordinateSet.height)
    };
    if (coordinates[nodeId]!.x !== next.x || coordinates[nodeId]!.y !== next.y) {
      coordinates[nodeId] = next;
      moved.add(nodeId);
    }
  }
  if (moved.size === 0) return document;
  const overrides = { ...document.depiction.bondLengthOverrides };
  for (const edge of document.structure.graph.edges) {
    if ((!moved.has(edge.source) && !moved.has(edge.target)) || overrides[edge.id]) continue;
    const oldSource = coordinateSet.coordinates[edge.source];
    const oldTarget = coordinateSet.coordinates[edge.target];
    const nextSource = coordinates[edge.source];
    const nextTarget = coordinates[edge.target];
    if (
      oldSource &&
      oldTarget &&
      nextSource &&
      nextTarget &&
      Math.abs(distanceBetween(nextSource, nextTarget) - distanceBetween(oldSource, oldTarget)) >
        BOND_LENGTH_OVERRIDE_TOLERANCE
    ) {
      overrides[edge.id] = true;
    }
  }
  return changeDepiction(document, {
    coordinateSet: { ...coordinateSet, coordinates },
    bondLengthOverrides: overrides
  });
}

/**
 * #1047 -- a flip turns the drawing over (180 degrees about the flip axis) rather than mirroring
 * it: a mirror image drawn with its wedges unchanged is the enantiomer. Swaps solid and hashed
 * wedges drawn from the flipped atoms, so every stereocentre keeps its configuration.
 */
export function swapWedgesFrom(document: DrawingDocument, atomIds: ReadonlySet<string>): DrawingDocument {
  let changed = false;
  const bondStyles = Object.fromEntries(
    Object.entries(document.depiction.bondStyles).map(([id, style]) => {
      if ((style.style !== 'solid-wedge' && style.style !== 'hashed-wedge') || !atomIds.has(style.fromAtomId)) {
        return [id, style];
      }
      changed = true;
      return [id, { ...style, style: style.style === 'solid-wedge' ? 'hashed-wedge' : 'solid-wedge' }];
    })
  ) as DrawingDocument['depiction']['bondStyles'];
  return changed ? changeDepiction(document, { bondStyles }) : document;
}
